// Cellery Soapbox — auth context (CONTRACTS §3).
// Google popup sign-in, @cellery.ai domain gate, and session-cookie sync with
// the `session` Cloud Function so the same-origin iframe can load gated
// /content/** behind a __session cookie.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { ALLOWED_DOMAIN } from "./types";

interface AuthState {
  user: User | null;
  emailLower: string;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const DOMAIN_SUFFIX = `@${ALLOWED_DOMAIN.toLowerCase()}`;
const DOMAIN_ERROR = `Access is limited to @${ALLOWED_DOMAIN} accounts. Use your Cellery Google account.`;

/** A user passes the gate iff verified and lowercased email ends with @cellery.ai. */
function isCelleryUser(user: User): boolean {
  const emailLower = (user.email ?? "").toLowerCase();
  return user.emailVerified && emailLower.endsWith(DOMAIN_SUFFIX);
}

/** POST the fresh ID token to set the __session cookie (CONTRACTS §2/§3). */
async function postSession(user: User): Promise<void> {
  const idToken = await user.getIdToken();
  const res = await fetch("/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw new Error(`session sync failed (${res.status})`);
  }
}

/** DELETE clears the __session cookie (sign-out). */
async function deleteSession(): Promise<void> {
  try {
    await fetch("/session", { method: "DELETE", credentials: "same-origin" });
  } catch {
    // Best-effort; local sign-out still proceeds.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (next) => {
      if (!next) {
        setUser(null);
        setLoading(false);
        return;
      }

      if (!isCelleryUser(next)) {
        // Reject non-@cellery.ai: drop any cookie and sign out locally.
        setError(DOMAIN_ERROR);
        await deleteSession();
        await firebaseSignOut(auth);
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        await postSession(next);
        setUser(next);
        setError(null);
      } catch {
        setError("Could not establish a secure session. Try signing in again.");
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const signIn = useCallback(async () => {
    setError(null);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      // §3 step 2: explicit gate at sign-in; onIdTokenChanged also enforces it.
      if (!isCelleryUser(cred.user)) {
        await deleteSession();
        await firebaseSignOut(auth);
        setError(DOMAIN_ERROR);
      }
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        return; // user dismissed the popup; not an error worth surfacing
      }
      setError("Sign-in failed. Please try again.");
    }
  }, []);

  const signOut = useCallback(async () => {
    await deleteSession();
    await firebaseSignOut(auth);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      emailLower: (user?.email ?? "").toLowerCase(),
      loading,
      error,
      signIn,
      signOut,
      clearError,
    }),
    [user, loading, error, signIn, signOut, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
